import {AxisConfiguration, InMemoryAxisDataService} from "@o2d/datasource";
import type {LayoutTemplate} from "@o2d/grid-layout";
import {DateTime} from "luxon";
import {BackendAxisDataService} from "../../services/backend-axis-data.service";
import type {TimelineViewConfig} from "./timeline-view.config";
import {deriveXAxisSections} from "./timeline-layout";
import {CALENDAR_PROJECT_UID} from "../../constants";

/**
 * Creates axis configurations for a timeline view.
 * Combines shared X-axes and header Y-axes with entity-specific Y-axis.
 *
 * The two X axes are DERIVED from the layout template rather than spelled out here, so a
 * view that adds a fixed column (the board view's milestone column, template t4) needs no
 * change in this file. Only axisX is derived — see the note on the Y axes below.
 *
 * @param cursorX - Date reference point for the timeline
 * @param config - Entity-specific configuration for the Y-axis
 * @param layoutTemplate - The view's layout template; supplies the column structure.
 *                         Required on purpose, with no default: a default would silently
 *                         allow the one failure this derivation exists to prevent —
 *                         rendering t4 while deriving t3's columns.
 */
export const createTimelineAxisConfigurations = (
    cursorX: DateTime,
    config: TimelineViewConfig,
    layoutTemplate: LayoutTemplate
): AxisConfiguration<string>[] => {

const {fixedSectionIds, fixedValues, fluidSectionId} = deriveXAxisSections(layoutTemplate);

return [
    // X-axis: the fixed row-header columns (t3: title, add-issue, context menu;
    // t4 additionally: add-milestone). Its values are identity counters — only how many
    // there are matters, never what they are.
    {
        axis: 'x',
        axisId: 'A',
        sectionIds: fixedSectionIds,
        scrollable: false,
        dataService: {
            dataServiceId: 'dummy-x-axis',
            type: InMemoryAxisDataService,
            debug: false,
            options: {
                // A closure, not the array: InMemoryAxisDataService calls the generator.
                generator: () => fixedValues
            }
        }
    },
    // X-axis: DateTime timeline (the single fluid column)
    {
        axis: 'x',
        axisId: 'B',
        sectionIds: [fluidSectionId],
        scrollable: true,
        dataService: {
            dataServiceId: 'datetime-axis',
            type: InMemoryAxisDataService,
            debug: false,
            options: {
                generator: () => {
                    const values: string[] = [];
                    for (let i = -365; i <= 365; i++) {
                        values.push(cursorX.plus({days: i}).toISODate({precision: `day`})!);
                    }
                    return values;
                }
            }
        }
    },
    // --- Y axes: deliberately NOT derived from the template. ---
    // Three independent reasons, any one of them sufficient. (1) The fixed row sections map
    // to FIVE separate axes (C0..C3 for the date headers, C4 for the calendar row), not to
    // one aggregate axis the way the columns do. (2) `row-section-space` is a fixed section
    // with neither an axis nor an area; a symmetric derivation would pull it in, and the
    // grid would then look up an area that does not exist. (3) C4 and D are backend-backed
    // and have no generator to derive at all.
    // Y-axis: Month/Year header (row-section-0)
    {
        axis: 'y',
        axisId: 'C0',
        sectionIds: ['row-section-0'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-0',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Calendar week header (row-section-1)
    {
        axis: 'y',
        axisId: 'C1',
        sectionIds: ['row-section-1'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-1',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Weekday header (row-section-2)
    {
        axis: 'y',
        axisId: 'C2',
        sectionIds: ['row-section-2'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-2',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Day of month header (row-section-3)
    {
        axis: 'y',
        axisId: 'C3',
        sectionIds: ['row-section-3'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-3',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Calendar row (row-section-calendar) - backend-fetched so axisEntityMetadata is populated
    {
        axis: 'y',
        axisId: 'C4',
        sectionIds: ['row-section-calendar'],
        scrollable: false,
        dataService: {
            dataServiceId: 'calendar-y-axis',
            type: BackendAxisDataService,
            debug: false,
            options: {
                entityType: 'Project',
                sort: { strategy: 'property', path: 'title', direction: 'ASC' },
                queryParams: [{ key: 'uid', comparator: 'eq', value: CALENDAR_PROJECT_UID }]
            } as unknown as Record<string, unknown>
        }
    },
    // Y-axis: Entity rows (row-section-4) - backend-based
    {
        axis: 'y',
        axisId: 'D',
        sectionIds: ['row-section-4'],
        scrollable: true,
        dataService: {
            dataServiceId: `${config.entityType.toLowerCase()}-axis`,
            type: BackendAxisDataService,
            debug: false,
            // Cast required: AxisDataServiceConfiguration defaults options to Record<string, unknown>
            // but BackendAxisDataServiceOptions intentionally has no index signature for type safety.
            options: {
                entityType: config.entityType,
                sort: config.sort,
                onFirstFetch: config.onFirstFetch,
                ...(config.queryParams?.length ? { queryParams: config.queryParams } : {})
            } as unknown as Record<string, unknown>
        }
    }
];
};
